import { z } from 'zod';
import { defineContract } from './contract';

/** Bearer sessions for mobile clients (ADR 0014): short-lived access token + rotating refresh token. */
export const tokenPairSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.string(),
});
export type TokenPairResponse = z.infer<typeof tokenPairSchema>;

export const tokenSignInBody = z.strictObject({
  email: z.email(),
  password: z.string(),
  /** Shown in the user's device list; free text, max 100 characters. */
  deviceLabel: z.string().max(100).nullish(),
});
export type TokenSignInBody = z.infer<typeof tokenSignInBody>;

/** Refresh tokens are 43 characters; the bound only stops oversized input early. */
export const refreshTokenBody = z.strictObject({ refreshToken: z.string().min(1).max(256) });
export type RefreshTokenBody = z.infer<typeof refreshTokenBody>;

/** `GET /auth/providers`: which sign-in methods this product offers (the web UI shows only these). */
export const authProvidersSchema = z.object({
  providers: z.array(z.string()),
  /** Email one-time code sign-in is enabled. */
  emailCode: z.boolean(),
});
export type AuthProvidersResponse = z.infer<typeof authProvidersSchema>;

/**
 * Email one-time code sign-in (ADR 0014). The request always answers 202 with the same body,
 * whether or not the address has an account.
 */
export const otpRequestBody = z.strictObject({
  email: z.email().max(254),
  /** Language of the email; unsupported values fall back to English. */
  locale: z.string().max(35).nullish(),
});
export type OtpRequestBody = z.infer<typeof otpRequestBody>;

export const otpRequestedSchema = z.object({ status: z.literal('accepted') });

/** Digits in any script (Arabic-Indic digits are normalized server-side); the bound stops oversized input. */
export const otpVerifyBody = z.strictObject({
  email: z.email().max(254),
  code: z.string().min(1).max(64),
  client: z.enum(['web', 'mobile']),
  /** Mobile only: shown in the user's device list. */
  deviceLabel: z.string().max(100).nullish(),
});
export type OtpVerifyBody = z.infer<typeof otpVerifyBody>;

/** `client: 'web'`: the session cookie is set and this body describes the user. */
export const otpWebSignedInSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    displayName: z.string().nullable(),
    preferredLocale: z.string().nullable(),
    preferredTimezone: z.string().nullable(),
  }),
  session: z.object({ expiresAt: z.string() }),
});
export type OtpWebSignedInResponse = z.infer<typeof otpWebSignedInSchema>;

export const authContracts = {
  providers: defineContract({
    method: 'GET',
    path: '/auth/providers',
    response: authProvidersSchema,
  }),
  otp: {
    request: defineContract({
      method: 'POST',
      path: '/auth/otp/request',
      body: otpRequestBody,
      response: otpRequestedSchema,
    }),
    /** Wrong, expired, locked and missing codes all answer `400 auth.code_invalid`. */
    verify: defineContract({
      method: 'POST',
      path: '/auth/otp/verify',
      body: otpVerifyBody,
      response: z.union([otpWebSignedInSchema, tokenPairSchema]),
    }),
  },
  tokens: {
    signIn: defineContract({
      method: 'POST',
      path: '/auth/token',
      body: tokenSignInBody,
      response: tokenPairSchema,
    }),
    refresh: defineContract({
      method: 'POST',
      path: '/auth/token/refresh',
      body: refreshTokenBody,
      response: tokenPairSchema,
    }),
    /** Always answers 204, whether or not the token was known. */
    revoke: defineContract({
      method: 'POST',
      path: '/auth/token/revoke',
      body: refreshTokenBody,
      response: null,
    }),
    revokeAll: defineContract({
      method: 'POST',
      path: '/auth/token/revoke-all',
      response: null,
    }),
  },
};
